import type {
  Enums,
  SupabaseClient,
  Tables,
} from "@repo/supabase/database-types"
import { logger } from "@repo/utils/logger"
import type {
  AddCurrencyData,
  AddSystemInvoiceItemData,
  EditSystemInvoiceItemData,
} from "~/modules/system-config/system-config.schema"

export type CurrencyStatus = Enums<"currency_status">
export type Currency = Tables<"currencies">
export type LocaleStatus = Enums<"locale_status">
export type Locale = Tables<"locales">
export type LanguageProficiencyLevelStatus =
  Enums<"language_proficiency_level_status">
export type LanguageProficiencyLevel = Tables<"language_proficiency_levels">
export type SystemInvoiceItemStatus = Enums<"system_invoice_item_status">
export type SystemInvoiceItem = Tables<"system_invoice_items">

export const createSystemConfigService = (supabase: SupabaseClient) => {
  return new SystemConfigService(supabase)
}

class SystemConfigService {
  log = logger.child({
    module: "system-config-service",
  })

  constructor(private readonly supabase: SupabaseClient) {}

  async getAvailableLanguageProficiencyLevels(
    status: LanguageProficiencyLevelStatus[] = ["active"],
  ) {
    const { data, error } = await this.supabase
      .from("language_proficiency_levels")
      .select("*")
      .in("status", status)
      .order("code", { ascending: true })

    if (error) {
      this.log.error("Error fetching language proficiency levels", { error })
      throw new Error(error.message)
    }

    return data
  }

  async getAvailableLocales(status: LocaleStatus[] = ["active"]) {
    const { data, error } = await this.supabase
      .from("locales")
      .select("*")
      .in("status", status)
      .order("code", { ascending: true })

    if (error) {
      this.log.error("Error fetching locales", { error })
      throw new Error(error.message)
    }

    return data
  }

  async getAvailableCurrencies(status: CurrencyStatus[] = ["active"]) {
    const { data, error } = await this.supabase
      .from("currencies")
      .select("*")
      .in("status", status)
      .order("code", { ascending: true })

    if (error) {
      this.log.error("Error fetching currencies", { error })
      throw new Error(error.message)
    }

    return data
  }

  async addCurrency({ code, name, symbol }: AddCurrencyData) {
    const { data, error } = await this.supabase
      .from("currencies")
      .insert([{ code, name, symbol, status: "active" }])
      .select()
      .single()

    if (error) {
      this.log.error("Error adding currency", { error })
      throw new Error(error.message)
    }

    return data
  }

  async getAvailableSystemInvoiceItems(
    status: SystemInvoiceItemStatus[] = ["active"],
  ) {
    const { data, error } = await this.supabase
      .from("system_invoice_items")
      .select("*")
      .in("status", status)
      .order("name", { ascending: true })

    if (error) {
      this.log.error("Error fetching system invoice items", { error })
      throw new Error(error.message)
    }

    return data
  }

  async getCurrencyExchangeRates({
    limit = 10,
    offset = 0,
    since,
  }: {
    limit?: number
    offset?: number
    since?: Date
  } = {}) {
    let query = this.supabase
      .from("exchange_rates")
      .select("*")
      .order("timestamp", { ascending: false })

    if (since) {
      query = query.gte("timestamp", since.toISOString())
    }

    query = query.range(
      Number(offset) * Number(limit),
      Number(offset + 1) * Number(limit) - 1,
    )

    const { data, error } = await query

    if (error) {
      this.log.error("Error fetching currency exchange rates", { error })
      throw new Error(error.message)
    }

    return data
  }

  async editCurrency({
    code,
    name,
    symbol,
  }: {
    code: string
    name: string
    symbol: string
  }) {
    const { data, error } = await this.supabase
      .from("currencies")
      .update({ name, symbol })
      .eq("code", code)
      .select()
      .single()

    if (error) {
      this.log.error("Error editing currency", { error })
      throw new Error(error.message)
    }

    return data
  }

  async updateCurrencyStatus({
    code,
    status,
  }: {
    code: string
    status: "active" | "inactive"
  }) {
    const { data, error } = await this.supabase
      .from("currencies")
      .update({ status })
      .eq("code", code)
      .select()
      .single()

    if (error) {
      this.log.error("Error updating currency status", { error })
      throw new Error(error.message)
    }

    return data
  }

  async addLocale({ code, name }: { code: string; name: string }) {
    const { data, error } = await this.supabase
      .from("locales")
      .insert({ code, name, status: "active" })
      .select()
      .single()

    if (error) {
      this.log.error("Error adding locale", { error })
      throw new Error(error.message)
    }

    return data
  }

  async editLocale({ code, name }: { code: string; name: string }) {
    const { data, error } = await this.supabase
      .from("locales")
      .update({ name })
      .eq("code", code)
      .select()
      .single()

    if (error) {
      this.log.error("Error editing locale", { error })
      throw new Error(error.message)
    }

    return data
  }

  async updateLocaleStatus({
    code,
    status,
  }: {
    code: string
    status: "active" | "inactive"
  }) {
    const { data, error } = await this.supabase
      .from("locales")
      .update({ status })
      .eq("code", code)
      .select()
      .single()

    if (error) {
      this.log.error("Error updating locale status", { error })
      throw new Error(error.message)
    }

    return data
  }

  async addLanguageProficiencyLevel({
    code,
    name,
  }: {
    code: string
    name: string
  }) {
    const { data, error } = await this.supabase
      .from("language_proficiency_levels")
      .insert({ code, name, status: "active" })
      .select()
      .single()

    if (error) {
      this.log.error("Error adding language proficiency level", { error })
      throw new Error(error.message)
    }

    return data
  }

  async editLanguageProficiencyLevel({
    code,
    name,
  }: {
    code: string
    name: string
  }) {
    const { data, error } = await this.supabase
      .from("language_proficiency_levels")
      .update({ name })
      .eq("code", code)
      .select()
      .single()

    if (error) {
      this.log.error("Error editing language proficiency level", { error })
      throw new Error(error.message)
    }

    return data
  }

  async updateLanguageProficiencyLevelStatus({
    code,
    status,
  }: {
    code: string
    status: "active" | "inactive"
  }) {
    const { data, error } = await this.supabase
      .from("language_proficiency_levels")
      .update({ status })
      .eq("code", code)
      .select()
      .single()

    if (error) {
      this.log.error("Error updating language proficiency level status", {
        error,
      })
      throw new Error(error.message)
    }

    return data
  }

  async addSystemInvoiceItem(item: AddSystemInvoiceItemData) {
    const { data, error } = await this.supabase
      .from("system_invoice_items")
      .insert({ ...item, status: "active" })
      .select()
      .single()

    if (error) {
      this.log.error("Error adding system invoice item", { error })
      throw new Error(error.message)
    }

    return data
  }

  async editSystemInvoiceItem({ id, ...item }: EditSystemInvoiceItemData) {
    const { data, error } = await this.supabase
      .from("system_invoice_items")
      .update(item)
      .eq("id", id)
      .select()
      .single()

    if (error) {
      this.log.error("Error editing system invoice item", { error })
      throw new Error(error.message)
    }

    return data
  }

  async updateSystemInvoiceItemStatus({
    id,
    status,
  }: {
    id: string
    status: "active" | "inactive"
  }) {
    const { data, error } = await this.supabase
      .from("system_invoice_items")
      .update({ status })
      .eq("id", id)
      .select()
      .single()

    if (error) {
      this.log.error("Error updating system invoice item status", { error })
      throw new Error(error.message)
    }

    return data
  }
}
